import type { ChatMessage } from "schemas/generated/chatMessage";

import React from "react";

import { skipToken } from "@tanstack/react-query";
import { fileVersionRouter } from "@v2/trpc/routes/fileVersion";

/**
 * This function is used to look through chat messages and find which page to show in the editor (if any).
 * We take the first of the following:
 *  - Last edited page
 *  - Last created page from a selected template
 *  - Last created page from a page generation
 */
export const useFindLastEditedPageIdFromMessages = (
  messages: ChatMessage[],
): string | null => {
  const result = React.useMemo(() => {
    // Note (Noah, 2025-08-19): Sort messages so that the most recent message is first, since
    // we want to make sure that the page the user has most recently edited using the chat is
    // the one that's returned from this function
    const sortedMessages = [...messages].sort(
      (a, b) => b.createdAt.getTime() - a.createdAt.getTime(),
    );
    for (const message of sortedMessages) {
      if (message.content.some((content) => content.fileId)) {
        for (const content of message.content) {
          if (content.fileId) {
            return { type: "fileId", id: content.fileId };
          }
        }
      }
    }

    return null;
  }, [messages]);

  // Fetch the file version using trpc if we found a fileVersionId
  const { data: fileVersion } = fileVersionRouter.get.useQuery(
    result?.type === "fileVersion"
      ? {
          id: result.id,
          shouldIncludeDeleted: true,
        }
      : skipToken,
  );

  // Return fileId directly if selectedTemplate, or from fileVersion if tool message
  if (result?.type === "fileId") {
    return result.id;
  }
  return fileVersion?.fileId ?? null;
};
